/**
 * Configuration object for various AI platforms and services.
 * Reranked by category popularity based on 2025 market trends and usage data.
 * The ranking prioritizes the most widely used and searched AI categories.
 * All objects from the original configuration are preserved.
 */
const platformConfig: any = {
  aiCostsAI: {
    key: "aiCostsAI",
    name: "AICosts.ai",
    type: "AI Cost Management Platform",
    category:
      "AI Analytics, Cost Management, Billing Optimization, Spending Tracker",
    description:
      "A unified dashboard for tracking, analyzing, and optimizing spending across all AI services from LLMs like Claude, GPT, and Gemini to AI workflow tools like Make, Zapier, and n8n, plus specialized AI services like Tavily, Pinecone, and RunwayML.",
    image: "https://aicosts.ai/assets/images/logo.png",
  },
  adInsightsAI: {
    key: "adInsightsAI",
    name: "AdInsights.ai",
    type: "Strategic AI Marketing Platform",
    category:
      "AI Marketing, Ad Management, Analytics, Audience Intelligence, Strategy Generation",
    description:
      "A strategic AI marketing platform for small businesses, offering AI-powered multi-platform ad campaign management and optimization, ICP research, strategy generation, and analytics.",
    image: "https://www.adinsights.ai/logo.png",
  },
  goViralStrategy: {
    key: "goViralStrategy",
    name: "GoViralStrategy.com",
    type: "AI Social Media Tool",
    category: "AI Content Generator, Short-Form Video, Social Media Automation",
    description:
      "Offers AI-driven tools to assist short-form video creators in optimizing their content for platforms like TikTok and Instagram.",
    image: "https://goviralstrategy.com/goviralstrategy-logo.png",
  },

  // CATEGORY 1: GENERATIVE AI & CHATBOTS (Most popular category in 2025)
  openAI: {
    key: "openAI",
    name: "OpenAI",
    type: "AI Platform",
    category: "Generative AI, Chatbot, Agent, Large Language Models",
    description:
      "Leading provider of large language models for text generation, chatbots, coding, and more. Offers various models via API.",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f4/OpenAI_Logo.svg/1200px-OpenAI_Logo.svg.png",
  },
  claude: {
    key: "claude",
    name: "Anthropic Claude",
    type: "AI Platform",
    category: "Generative AI, Chatbot, Large Language Models",
    description:
      "Develops large language models known for their helpful, honest, and harmless nature. Offers models like Claude 3 via API.",
    image:
      "https://seeklogo.com/images/A/anthropic-logo-5A3D32E90E-seeklogo.com.png",
  },
  gemini: {
    key: "gemini",
    name: "Gemini",
    type: "AI Platform",
    category: "Generative AI, Chatbot, Large Language Models, Multimodal AI",
    description:
      "Google's family of large language models, integrated into various Google products and available via Google Cloud's Vertex AI and the Gemini API.",
    image: "https://www.gstatic.com/lamda/images/gemini_720x720_icon.png",
  },
  grok: {
    key: "grok",
    name: "Grok",
    type: "AI Chatbot/Model",
    category: "Generative AI, Chatbot, Large Language Models, AI Assistant",
    description:
      "xAI's cosmic guide, Grok is xAI's most advanced model, blending superior reasoning with extensive pretraining knowledge. Available on grok.com, iOS, and Android.",
    image: "https://x.ai/grok-logo.png", // Placeholder, as no direct logo URL was found but a common practice is /logo.png or similar.
  },
  cohere: {
    key: "cohere",
    name: "Cohere",
    type: "AI Platform",
    category: "AI Platform, Generative AI, Large Language Models",
    description:
      "Provides LLMs focused on enterprise use cases, including generation, summarization, embeddings, and search.",
    image:
      "https://seeklogo.com/images/C/cohere-logo-C1817C299B-seeklogo.com.png",
  },
  huggingface: {
    key: "huggingface",
    name: "Hugging Face",
    type: "AI Platform",
    category: "AI Platform, Open Source Community, Machine Learning",
    description:
      "A hub for open-source AI models, datasets, and applications. Offers inference APIs for hosting and running models.",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b2/Hugging_Face_logo.svg/1200px-Hugging_Face_logo.svg.png",
  },
  rekaAI: {
    key: "rekaAI",
    name: "Reka AI",
    type: "AI Research Lab/Model Provider",
    category: "AI Platform, Multimodal AI, Large Language Models",
    description:
      "An AI research company focused on building powerful multimodal language models.",
    image: "https://reka.ai/static/button-logo.png",
  },
  vertexai: {
    key: "vertexai",
    name: "Vertex AI",
    type: "Cloud Platform",
    category: "AI Platform, Cloud Services, Machine Learning, Generative AI",
    description:
      "Google Cloud's unified platform for building, deploying, and scaling ML models, including access to Google's own models and others.",
    image:
      "https://cloud.google.com/static/images/products/vertex-ai/vertex-ai-icon.svg",
  },
  microsoftazureai: {
    key: "microsoftazureai",
    name: "Microsoft Azure AI",
    type: "Cloud Platform",
    category: "Conversational AI, LLMs, various AI services, Cloud Services",
    description:
      "Microsoft's suite of AI services on Azure, including OpenAI models (Azure OpenAI Service), Bot Service, Machine Learning, etc.",
    image:
      "https://seeklogo.com/images/M/microsoft-azure-ai-logo-B6661315E5-seeklogo.com.png",
  },
  minimax: {
    key: "minimax",
    name: "Minimax",
    type: "AI Platform",
    category:
      "Generative AI, AI Assistant, Chatbot, Image to Video, Large Language Models",
    description:
      "An AI company developing large-scale AI models and applications, including an AI assistant with search, creativity, and analysis capabilities, and an image-to-video API.",
    image:
      "https://play-lh.googleusercontent.com/Lq1j_j_P4v2lX1M_t3c3g4_W7w9M_h0c5f5z8f9l8k9k0b0j0j9j0j0j0j0j0j0j0",
  },
  fireworksAI: {
    key: "fireworksAI",
    name: "Fireworks AI",
    type: "AI Platform",
    category:
      "AI Platform, Generative AI, LLM Inference, Image Generation, Speech-to-Text, Developer Tool",
    description:
      "A platform providing fast and affordable inference for generative AI models, including large language models, image generation, and speech-to-text, with a focus on developers and enterprise use cases.",
    image: "https://fireworks.ai/static/logo.png",
  },
  anyscale: {
    key: "anyscale",
    name: "Anyscale",
    type: "AI Platform",
    category:
      "AI Platform, Distributed Computing, Machine Learning, Developer Tool",
    description:
      "A platform for building, running, and scaling AI applications, leveraging the Ray distributed computing framework.",
    image: "https://www.anyscale.com/hubfs/anyscale-logo.svg",
  },
  stabilityai: {
    key: "stabilityai",
    name: "Stability AI",
    type: "AI Platform",
    category: "AI Platform, Creative AI, Image Generation, Open Source",
    description:
      "Known for its open-source image generation models (Stable Diffusion) and offers API access.",
    image:
      "https://seeklogo.com/images/S/stability-ai-logo-D66B81331B-seeklogo.com.png",
  },
  togetherAI: {
    key: "togetherAI",
    name: "Together AI",
    type: "AI Platform, Cloud Platform, AI Model Hosting/API",
    category:
      "AI Platform, Generative AI, LLM Inference, Model Hosting, API, Cloud Services, Fine-tuning",
    description:
      "Together AI is a cloud platform providing fast and scalable infrastructure for training, fine-tuning, and running generative AI models. It offers access to a wide range of open-source models, inference services, and tools for developers to build and deploy AI applications.",
    image:
      "https://images.prismic.io/together-ai/ZGFmYTU3Yy1jZTg5LTRlNTUtYjQ0Yy05YmMyOTk1ZmRjNzQ_logo-dark.svg?auto=format%2Ccompress&fit=max",
  },
  ollama: {
    key: "ollama",
    name: "Ollama",
    type: "Local LLM Runner",
    category: "Large Language Models, Local AI, Developer Tool, Open Source",
    description:
      "A lightweight framework for running large language models locally on your machine, enabling privacy-focused and customizable AI applications.",
    image: "https://ollama.ai/public/ollama.png", // Assuming a common logo path
  },
  awsbedrock: {
    key: "awsbedrock",
    name: "AWS Bedrock",
    type: "Cloud AI Service",
    category:
      "Generative AI, Large Language Models, Cloud Services, AI Platform",
    description:
      "Amazon Web Services (AWS) Bedrock is a fully managed service that makes foundation models from Amazon and leading AI startups available via an API, along with tools for building and scaling generative AI applications.",
    image:
      "https://d1.awsstatic.com/logos/products/AWS-Bedrock_Light-bg.e3d23b9d885a0650d53c7c8c83a7c64539ef444b.png", // Common AWS logo style
  },
  vercelAI: {
    key: "vercelAI",
    name: "Vercel AI",
    type: "AI Development Platform",
    category:
      "AI Development, Web Development, Deployment, Serverless Functions",
    description:
      "Vercel provides tools and infrastructure for building and deploying AI-powered web applications, including serverless functions for running AI models and integrations with various AI services.",
    image:
      "https://assets.vercel.com/image/upload/v1672322312/nextjs/social-cards/ai.jpg", // Using a relevant Vercel AI social image
  },
  groq: {
    key: "groq",
    name: "Groq",
    type: "AI Inference Chip/Platform",
    category: "AI Hardware, LLM Inference, High-Performance Computing",
    description:
      "Specializes in ultra-fast inference for large language models using its custom Language Processing Unit (LPU) inference engine.",
    image: "https://groq.com/static/images/logo.png", // Common logo path
  },
  deepinfra: {
    key: "deepinfra",
    name: "Deepinfra",
    type: "AI Inference Platform",
    category: "AI Inference, Model Hosting, Developer Tool, Open Source",
    description:
      "A platform offering fast and cost-effective inference for open-source large language models and other AI models, democratizing access to powerful AI infrastructure.",
    image: "https://deepinfra.com/logo.svg", // Common logo path
  },
  openrouter: {
    key: "openrouter",
    name: "OpenRouter",
    type: "Unified AI API",
    category: "AI API, LLM Gateway, Model Routing, Developer Tool",
    description:
      "A unified API that allows developers to access and switch between various large language models from different providers (OpenAI, Anthropic, Google, etc.) with a single interface.",
    image: "https://openrouter.ai/logo.png", // Common logo path
  },
  litellm: {
    key: "litellm",
    name: "LiteLLM",
    type: "LLM API Wrapper",
    category: "LLM API, Developer Tool, Multi-model API",
    description:
      "A lightweight Python library that unifies the APIs for various large language models, allowing developers to easily switch between models and providers.",
    image: "https://litellm.ai/img/logo.png", // Common logo path
  },
  msty: {
    key: "msty",
    name: "Msty",
    type: "Local & Online AI Model Interface",
    category:
      "AI Chatbot, Local AI, LLM Interface, AI Productivity Tools, AI Developer Tools",
    description:
      "A desktop application for Windows, Mac, and Linux that simplifies running and interacting with both local and online AI models (e.g., Hugging Face, Ollama, OpenAI, Claude). Features include parallel chats for model comparison, real-time web search, knowledge stacks for RAG, a prompts library, and an intuitive user interface, prioritizing privacy and offline functionality.",
    image: "https://msty.app/msty-logo.png", // Assuming a common logo path on their main site
  },

  // CATEGORY 2: AI CODING ASSISTANTS & DEVELOPER TOOLS
  cursor: {
    key: "cursor",
    name: "Cursor",
    type: "Agentic Coding Assistant",
    category: "Coding Assistant, AI IDE, Developer Tool",
    description:
      "An AI-powered code editor that enhances developer productivity with features like code generation, smart rewrites, and codebase queries.",
    image:
      "https://cdn.sanity.io/images/kt1b5w9c/production/bf53108821bf87a741e850e4f42c46d152714265-1200x800.png",
  },
  windsurf: {
    key: "windsurf",
    name: "Windsurf (formerly Codeium)",
    type: "Agentic Coding Assistant",
    category: "Coding Assistant, AI Code Completion, Developer Tool",
    description:
      "An AI code acceleration toolkit providing code completion in various languages and integrating into popular IDEs (formerly Codeium).",
    image:
      "https://codeium.com/brand/Suggested%20Logos/Wordmark/Codeium_Wordmark_Dark.png",
  },
  devin: {
    key: "devin",
    name: "Devin",
    type: "Agentic Coding Assistant",
    category: "AI Software Developer, Coding Agent",
    description:
      "The first AI software engineer capable of completing complex engineering tasks autonomously.",
    image: "https://devin.ai/with-cognition-text.png",
  },
  codebuff: {
    key: "codebuff",
    name: "Codebuff",
    type: "Agentic Coding Assistant",
    category: "AI Agent, CLI Tool, Coding Assistant",
    description:
      "A CLI-based coding agent that allows users to interact with their codebase and execute terminal commands using natural language.",
    image:
      "https://www.ycombinator.com/assets/discover/codebuff-a57d4a0b868c71660123e51a680316a4f2b42c10704741fd4238b50e39f7f1a2.jpg",
  },
  graphite: {
    key: "graphite",
    name: "Graphite",
    type: "AI Code Reviewer",
    category: "Code Review, Developer Tool, AI Assistant",
    description:
      "An AI-powered code review tool that provides immediate feedback on pull requests to improve code quality.",
    image:
      "https://graphite.dev/_next/image?url=%2F_next%2Fstatic%2Fmedia%2Fgraphite-logo.9d84a853.png&w=3840&q=75",
  },
  v0dev: {
    key: "v0dev",
    name: "v0.dev",
    type: "Frontend Design Tool",
    category: "AI Design, Frontend Development, UI Generation",
    description:
      "Vercel's generative UI tool that creates React components with Tailwind CSS from natural language prompts.",
    image: "https://v0.dev/social-image.png",
  },
  replit: {
    key: "replit",
    name: "Replit",
    type: "Coding Agent Platform",
    category: "Online IDE, Coding Agent, Application Development",
    description:
      "An online IDE with an integrated AI agent that helps users build, run, and deploy applications from their browser.",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7e/Replit_logo.svg/1200px-Replit_logo.svg.png",
  },
  boltnew: {
    key: "boltnew",
    name: "Bolt.new",
    type: "Coding Agent Platform",
    category: "Full-Stack Development, AI Agent, Online IDE",
    description:
      "An in-browser AI web development agent that allows users to prompt, run, edit, and deploy full-stack applications.",
    image: "https://bolt.new/social-image.png",
  },
  lovabledev: {
    key: "lovabledev",
    name: "Lovable.dev",
    type: "Coding Agent Platform",
    category: "AI App Builder, No-code/Low-code, Full-Stack Development",
    description:
      "An AI-powered app builder that helps users create full-stack web applications from natural language descriptions without writing code.",
    image:
      "https://www.lovable.dev/wp-content/uploads/2023/06/lovable-logo-color.png",
  },
  llamaindexAI: {
    key: "llamaindexAI",
    name: "LlamaIndex",
    type: "AI Data Framework",
    category: "AI Development, Data Orchestration, RAG, LLM Tools",
    description:
      "A data framework for LLM applications to ingest, structure, and access private or domain-specific data.",
    image:
      "https://storage.googleapis.com/gh-contact-assets/llamaindex-logo-asset/llamaindex-logo.svg",
  },
  openPipe: {
    key: "openPipe",
    name: "OpenPipe",
    type: "LLM Fine-tuning and Monitoring Platform",
    category: "LLMOps, Fine-tuning, Model Monitoring, Developer Tool",
    description:
      "A platform for fine-tuning and monitoring large language models (LLMs) to reduce cost and latency. Offers data capture, request logging, fine-tuning services, model hosting, caching, and evaluation tools.",
    image: "https://openpipe.ai/og/openpipe-og-new.jpg",
  },
  weightsBiases: {
    key: "weightsBiases",
    name: "Weights & Biases",
    type: "AI Developer Platform",
    category:
      "MLOps, LLMOps, Experiment Tracking, Model Management, Evaluation, Developer Tool",
    description:
      "The AI developer platform to build AI agents, applications, and models with confidence. Provides tools for experiment tracking, dataset and model versioning, hyperparameter optimization, evaluation, and collaborative dashboards.",
    image: "https://wandb.ai/site/assets/images/landscape_primary-a310f450.svg",
  },
  magicPatterns: {
    key: "magicPatterns",
    name: "Magic Patterns",
    type: "AI Prototyping Tool",
    category:
      "AI Design, Frontend Development, UI Generation, Prototyping, Developer Tool, Collaboration Tool",
    description:
      "An AI-powered platform that helps product teams, designers, and developers rapidly prototype ideas, generate UI designs from text prompts or screenshots, collaborate in real-time, and export designs to code (React) or Figma.",
    image: "https://www.magicpatterns.com/magicpatterns-logo.png", // Using a likely path based on common practices
  },
  instructorAI: {
    key: "instructorAI",
    name: "Instructor",
    type: "AI Library/Tool",
    category:
      "LLM Orchestration, Function Calling, Developer Tool, Python Library",
    description:
      "A Python library for interacting with LLMs more effectively, especially for structured output and function calling, enhancing their use in applications.",
    image:
      "https://pydantic-instructor.readthedocs.io/en/latest/_static/logo_instructor.svg", // Found a relevant logo for Instructor (Pydantic-Instructor)
  },
  hyperbolic: {
    key: "hyperbolic",
    name: "Hyperbolic AI",
    type: "AI Solutions Provider",
    category: "AI Solutions, Custom AI Development",
    description:
      "A company focused on delivering custom AI solutions and research.",
    image: "", // Placeholder, if a specific logo isn't easily found, it's best to leave it empty or note.
  },
  continueDev: {
    key: "continueDev",
    name: "Continue.dev",
    type: "Open Source AI Coding Assistant",
    category:
      "Coding Assistant, AI Code Completion, Developer Tool, Open Source, IDE Extension",
    description:
      "An open-source AI code assistant that integrates with VS Code and JetBrains IDEs, providing customizable autocomplete, chat, and edit features. It supports any model (OpenAI, Anthropic, local models), allows deep customization via config files, and includes features like codebase context, documentation integration, and development data collection for model improvement.",
    image: "https://continue.dev/static/images/continue-logo.png",
  },

  // CATEGORY 3: IMAGE & VIDEO GENERATION
  runwayml: {
    key: "runwayml",
    name: "RunwayML",
    type: "Creative AI Platform",
    category:
      "Generative AI, Video Generation, Image Generation, Multimedia Editing",
    description:
      "An AI-powered platform for creative professionals to generate and edit video, images, and other multimedia content, known for models like Gen-1, Gen-2, and Gen-3 Alpha.",
    image:
      "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fd/Runway_Logo.png/1200px-Runway_Logo.png",
  },
  fluxAI: {
    key: "fluxAI",
    name: "Flux AI",
    type: "Creative AI Platform",
    category: "Generative AI, Image Generation, Video Generation",
    description:
      "An AI platform offering various models for generating high-quality images and videos with features like text-to-image, image-to-image, and video effects.",
    image:
      "https://brandfetch.com/assets/logos/flux.ai-logo-full-0a3833646600268b6ee44c414855615d.png",
  },
  ideogramAI: {
    key: "ideogramAI",
    name: "Ideogram AI",
    type: "Generative AI Service",
    category: "Generative AI, Image Generation, Typography AI, Creative AI",
    description:
      "A text-to-image AI model known for its strong typography capabilities and ability to generate visually appealing graphics from text prompts.",
    image: "https://ideogram.ai/images/ideogram-social-preview.png",
  },
  krea: {
    key: "krea",
    name: "Krea",
    type: "Ad Creative Tool",
    category: "Creative AI, Image Generation, Video Generation, Design Tool",
    description:
      "An AI platform for generating and enhancing visual content, including images and videos, often used for ad creatives.",
    image: "https://krea.ai/krea-logo.svg",
  },
  piapi: {
    key: "piapi",
    name: "PiAPI",
    type: "AI API Provider",
    category:
      "Generative AI APIs, Text-to-Image, Face Swap, Text-to-Music, Text-to-Video",
    description:
      "Provides a single access point to various generative AI APIs, including unofficial APIs for models like Midjourney, Suno, and Dream Machine, as well as FaceSwap.",
    image: "https://piapi.ai/docs/piapi-logo.png",
  },
  autoshortsai: {
    key: "autoshortsai",
    name: "AutoShorts.ai",
    type: "AI Video Generator",
    category: "AI Content Generator, Text-to-Video, Social Media Automation",
    description:
      "Automates the creation, scheduling, and posting of faceless short videos for platforms like TikTok and YouTube, based on a provided topic.",
    image: "https://autoshorts.ai/assets/images/logo.png",
  },
  blotato: {
    key: "blotato",
    name: "Blotato",
    type: "AI Content Creation",
    category:
      "AI Content Generator, Text-to-Video, Social Media Automation, AI Agent Tool",
    description:
      "An AI content engine specializing in creating viral faceless videos and social media posts with automation capabilities via API.",
    image: "https://www.blotato.com/blotato-logo.png",
  },
  creatomate: {
    key: "creatomate",
    name: "Creatomate",
    type: "Creative Automation Platform",
    category: "Video Generation, Image Generation, Creative Automation, API",
    description:
      "A media automation platform with an API for developers and no-code users to automatically generate videos and images from templates and data.",
    image: "https://creatomate.com/assets/img/creatomate-logo.png",
  },
  heygen: {
    key: "heygen",
    name: "HeyGen",
    type: "AI Video Generation",
    category: "Generative AI, Video Generation, AI Avatars, Video Localization",
    description:
      "An AI video generation platform that allows users to create videos with AI avatars and voices, and localize videos into multiple languages.",
    image:
      "https://assets-global.website-files.com/63ea3f33451613578b5b0f7f/642f602a13936a7b3c9e10e4_Luma%20AI%20Logo.svg",
  },
  recraft: {
    key: "recraft",
    name: "Recraft",
    type: "AI Design Tool",
    category:
      "Creative AI, Image Generation, Vector Graphics, 3D Graphics, Design Tool",
    description:
      "An AI design tool for creating and editing vector and raster images, illustrations, and 3D graphics with a focus on consistent brand style.",
    image:
      "https://global-uploads.webflow.com/627a00f4a4ac843f8529275e/63792378686f1b3e8a713433_Granola%20Logo.png",
  },
  googleveo: {
    key: "googleveo",
    name: "Google Veo",
    type: "AI Video Generation Model",
    category: "Generative AI, Video Generation, Text to Video, Image to Video",
    description:
      "Google DeepMind's advanced AI model for generating high-quality videos from text and image prompts, available via Google Cloud's Vertex AI.",
    image:
      "https://cloud.google.com/static/images/products/vertex-ai/vertex-ai-icon.svg",
  },
  googleOpal: {
    key: "googleOpal",
    name: "Google Opal",
    type: "AI Mini-App Builder",
    category: "AI Platform, No-Code Development, Visual Workflow, AI Agent Tool",
    description:
      "Google Labs' experimental tool for building and sharing AI mini-apps that chain together prompts, models, and tools using simple natural language and visual editing, without writing code.",
    image: "https://developers.googleblog.com/static/blog/images/google-labs-icon.png",
  },
  klingAI: {
    key: "klingAI",
    name: "Kling AI",
    type: "Generative AI Creative Studio",
    category:
      "Generative AI, Image Generation, Video Generation, Audio Generation, Virtual Try-On",
    description:
      "A next-generation AI creative studio offering tools for image generation, video generation, sound generation, AI virtual try-on, lip sync, and image extension.",
    image: "https://klingai.com/global/logo.png",
  },
  lumaAI: {
    key: "lumaAI",
    name: "Luma AI",
    type: "Generative AI Platform",
    category: "Generative AI, 3D Reconstruction, NeRF, Video Generation",
    description:
      "Builds generative AI models for creating 3D content from videos and images, and for generating realistic video.",
    image:
      "https://assets-global.website-files.com/63ea3f33451613578b5b0f7f/642f602a13936a7b3c9e10e4_Luma%20AI%20Logo.svg",
  },
  ccsr: {
    key: "ccsr",
    name: "CCSR",
    type: "AI Upscaling Model",
    category: "Image Processing, Video Processing, Super Resolution, AI Model",
    description:
      "An AI model for consistent image and video super-resolution (upscaling) using a combination of diffusion models and GANs.",
    image: "https://www.runcomfy.com/static/img/logo.png",
  },
  replicate: {
    key: "replicate",
    name: "Replicate",
    type: "AI Model Hosting/API",
    category:
      "AI Platform, Generative AI, Image Generation, Model Hosting, API",
    description:
      "A platform that allows developers to run and fine-tune AI models with a cloud API, including models for image generation and more.",
    image: "https://replicate.com/static/logo.png",
  },
  klapApp: {
    key: "klapApp",
    name: "Klap.app",
    type: "AI Video Repurposing Tool",
    category:
      "AI Video Editor, Content Repurposing, Short-Form Video, Social Media Tool, Video Generation",
    description:
      "An AI-powered platform that transforms long-form videos into engaging, shareable short clips for platforms like TikTok, Reels, and YouTube Shorts, featuring auto-captioning, reframing, and highlight detection.",
    image: "https://klap.app/images/logo-full.svg",
  },
  falAI: {
    key: "falAI",
    name: "fal.ai",
    type: "AI Platform",
    category:
      "AI Platform, Real-time Inference, Image Generation, Developer Tool",
    description:
      "A platform offering highly optimized infrastructure for real-time AI inference, focusing on generative AI models like image generation, and providing low-latency APIs for developers.",
    image: "https://fal.ai/static/images/logo.png", // Assuming a common logo path
  },

  // CATEGORY 4: VOICE & SPEECH AI
  elevenlabs: {
    key: "elevenlabs",
    name: "Eleven Labs",
    type: "AI Voice Synthesis",
    category: "Voice AI, Audio Generation, Text-to-Speech, Voice Cloning",
    description:
      "A leading platform for AI-powered text-to-speech, voice cloning, and audio generation.",
    image:
      "https://seeklogo.com/images/E/elevenlabs-logo-D6D0623B03-seeklogo.com.png",
  },
  vapi: {
    key: "vapi",
    name: "Vapi",
    type: "Voice AI Toolkit",
    category: "Voice AI, AI Agent Toolkit",
    description:
      "A platform for building and deploying AI voice assistants that can handle phone calls and natural language conversations.",
    image: "https://www.vapi.ai/images/logo.png",
  },
  pipecat: {
    key: "pipecat",
    name: "Pipecat",
    type: "Voice and Multimodal AI Framework",
    category: "Voice AI, Multimodal AI, Open Source, AI Agent Framework",
    description:
      "An open-source Python framework for building real-time voice and multimodal conversational AI agents.",
    image:
      "https://github.com/pipecat-ai/pipecat/raw/main/docs/assets/logo.png",
  },
  wisprflow: {
    key: "wisprflow",
    name: "Wispr Flow",
    type: "Voice Input Tool",
    category: "Productivity, Voice Dictation, Writing Assistant",
    description:
      "A voice-powered dictation tool that allows users to write faster across applications with real-time transcription and editing.",
    image:
      "https://cdn.prod.website-files.com/64d4b70b7d3c2c492415e24w/64e350f2567f88f08f9882c5_Group%2015.png",
  },
  aqua: {
    key: "aqua",
    name: "Aqua",
    type: "Voice Input Tool",
    category: "Productivity, Voice Dictation, Contextual AI",
    description:
      "A fast AI dictation tool with contextual awareness that allows users to talk into any text field and supports custom words and commands.",
    image: "https://withaqua.com/logo.png",
  },
  assemblyAI: {
    key: "assemblyAI",
    name: "AssemblyAI",
    type: "AI API Provider",
    category: "Speech AI, Transcription, Natural Language Processing, AI API",
    description:
      "Provides APIs for converting audio and video into text, and for understanding and processing spoken language.",
    image:
      "https://www.assemblyai.com/assets/assemblyai-logo-vertical-lockup.svg",
  },
  sonautoAI: {
    key: "sonautoAI",
    name: "Sonauto.ai",
    type: "AI Audio Generation",
    category: "Audio Generation, Voice AI, Music Generation, Sound Design",
    description:
      "A platform for generating high-quality audio, including speech, music, and sound effects, using advanced AI models.",
    image: "https://sonauto.ai/assets/sonauto-logo.svg", // Assuming a common logo path
  },
  luvvoice: {
    key: "luvvoice",
    name: "LuvVoice.com",
    type: "AI Voice Synthesis",
    category: "Voice AI, Audio Generation, Text-to-Speech",
    description:
      "An online text-to-speech (TTS) tool that converts text into natural-sounding speech. It offers over 200 voices in more than 70 languages, utilizing advanced AI algorithms to generate high-quality, human-like voices. Users can input text, choose a voice, and download the resulting audio in MP3 format or listen to it directly. The service is suitable for content creators, students, and anyone needing text read aloud, with applications in media production, education, gaming, and accessibility services. It also supports converting documents like PDFs and TXTs to audio and allows for adjustment of speech rate and pitch.",
    image: "", // Image URL not found
  },

  // CATEGORY 5: AI AGENTS & AUTOMATION
  manusai: {
    key: "manusai",
    name: "Manus AI",
    type: "Autonomous AI Agent",
    category:
      "Autonomous AI, AI Agent, Workflow Automation, Multimodal AI, Productivity",
    description:
      "An autonomous artificial intelligence agent designed to independently plan and execute complex online tasks across various domains without direct human guidance.",
    image: "https://lobehub.com/icons/manus.svg",
  },
  gumloop: {
    key: "gumloop",
    name: "Gumloop",
    type: "AI Automation Platform",
    category:
      "AI Workflow Automation, No-code, Web Scraping, Browser Automation, Integration",
    description:
      "A no-code platform for building AI-powered workflow automations with a visual interface and integrations, enabling tasks like data extraction, categorization, and browser automation.",
    image: "https://www.gumloop.com/gumloop-logo.svg",
  },
  tavily: {
    key: "tavily",
    name: "Tavily",
    type: "AI Agent Tool",
    category: "Tool for AI Agent, Search API",
    description:
      "A search API specifically designed for AI agents, providing relevant and concise information from the web.",
    image:
      "https://assets-global.website-files.com/64f66003b6164960293f8b90/650241d80240b34b9250518d_logo.png",
  },
  recallAI: {
    key: "recallAI",
    name: "Recall.ai",
    type: "AI API Provider",
    category: "Meeting Productivity, AI Transcription, AI API, Developer Tool",
    description:
      "Provides a universal API for building meeting bots that can join meetings, get transcripts, and access real-time audio and video streams.",
    image: "https://www.recall.ai/assets/images/logo.png",
  },
  airtopAI: {
    key: "airtopAI",
    name: "Airtop.ai",
    type: "Cloud Browser Automation Platform",
    category: "AI Agent Tool, Browser Automation, Web Scraping, AI API",
    description:
      "A cloud browser platform enabling AI agents and automation tools to control web browsers in the cloud for seamless web interaction, including login, navigation, and data extraction, often via natural language instructions.",
    image: "https://www.airtop.ai/assets/images/logo.svg", // Found on their site
  },
  langgraphPlatform: {
    key: "langgraphPlatform",
    name: "LangGraph Platform",
    type: "AI Agent Deployment & Management Platform",
    category:
      "AI Agent, LLM Deployment, MLOps, Developer Tool, Agent Orchestration, Workflow Automation",
    description:
      "A purpose-built platform by LangChain for deploying, managing, and scaling long-running, stateful AI agents. It provides infrastructure for agent workflows, includes tools for visual development (LangGraph Studio), debugging, monitoring (integrates with LangSmith), and offers various deployment options (Cloud SaaS, hybrid, self-hosted). It focuses on reliability, controllability, streaming, and human-in-the-loop capabilities for complex agentic applications.",
    image: "https://www.langchain.com/assets/images/langchain-logo.svg",
  },

  // CATEGORY 6: AI ANALYTICS & MANAGEMENT PLATFORMS
  delphiAI: {
    key: "delphiAI",
    name: "Delphi AI",
    type: "AI Forecasting & Insights Platform", // Placeholder type, adjust as needed
    category:
      "AI Analytics, Business Intelligence, Predictive Analytics, Forecasting", // Placeholder categories, adjust as needed
    description:
      "Delphi AI is a platform designed to provide advanced AI-driven insights and forecasting capabilities for businesses, leveraging data to inform strategic decisions.", // Placeholder description, adjust as needed
    image: "https://www.delphi.ai/logo.png", // Assuming a common logo path; confirm if different
  },

  // CATEGORY 7: WORKFLOW AUTOMATION & iPaaS
  make: {
    key: "make",
    name: "Make",
    type: "iPaaS",
    category: "Low-code/No-code Automation, iPaaS, Integration",
    description:
      "A visual platform for building complex workflows and automating tasks by connecting various apps and services, including many AI tools.",
    image: "https://www.make.com/hubfs/make-logo-social-share.png",
  },
  zapier: {
    key: "zapier",
    name: "Zapier",
    type: "iPaaS",
    category: "No-code/Low-code Automation, iPaaS, Integration",
    description:
      "A popular no-code platform for connecting apps and automating workflows. Integrates with many AI services.",
    image: "https://cdn.zapier.com/zapier/images/icons/zapier-icon.png",
  },
  n8n: {
    key: "n8n",
    name: "n8n",
    type: "iPaaS",
    category: "Low-code/Developer-friendly Automation, iPaaS, Integration",
    description:
      "An open-source workflow automation platform that is highly extensible and connects to a vast number of services, including AI APIs.",
    image:
      "https://assets-global.website-files.com/630259b52209624224a81b26/630259b5220962d59ba81c21_n8n-logo.svg",
  },
  workato: {
    key: "workato",
    name: "Workato",
    type: "Enterprise iPaaS",
    category: "Enterprise iPaaS, Enterprise Automation, Low-code",
    description:
      "Enterprise iPaaS for connecting apps and automating workflows.",
    image:
      "https://www.workato.com/static/images/workato-logo-lockup-horizontal.svg",
  },
  bubble: {
    key: "bubble",
    name: "Bubble",
    type: "No-Code Development Platform",
    category:
      "No-code/Low-code Development, Web App Builder, Frontend, Backend, AI Integration",
    description:
      "A no-code development platform that allows users to design, build, and launch full-stack web and mobile applications without writing any code. It includes a drag-and-drop editor, built-in database, and logic functionality, with AI integration for app generation and design.",
    image:
      "https://bubble.io/cdn-cgi/image/f=auto,q=100/wp-content/uploads/2023/12/bubble-home-logo-2.svg",
  },
  xano: {
    key: "xano",
    name: "Xano",
    type: "No-Code Backend Development Platform",
    category:
      "No-code/Low-code Backend, Database, API Builder, Scalable Server",
    description:
      "A powerful no-code backend development platform that provides a scalable server, a flexible PostgreSQL database, and a fully-featured No-Code API builder. It enables users to build and scale application backends without writing code, offering robust data management and API creation capabilities.",
    image: "https://www.xano.com/xano-logo-transparent.svg",
  },

  // CATEGORY 8: VECTOR DATABASES & AI INFRASTRUCTURE
  pinecone: {
    key: "pinecone",
    name: "Pinecone",
    type: "Vector Database",
    category: "Vector Database, AI Agent Tool, Machine Learning",
    description:
      "Vector database for building high-performance vector search applications, often used with LLMs.",
    image: "https://www.vectorlogo.zone/logos/pineconeio/pineconeio-ar21.png",
  },
  higgsfieldAI: {
    key: "higgsfieldAI",
    name: "Higgsfield AI",
    type: "AI Video Generation Platform",
    category: "Generative AI, Video Generation, Text to Video, AI Research",
    description:
      "Higgsfield AI is an innovative platform specializing in high-quality video generation, leveraging advanced AI models to transform text into dynamic visual content. It aims to push the boundaries of creative AI and synthetic media.",
    image: "https://www.higgsfield.ai/assets/logo.png", // Assumed common path for logo
  },

  // CATEGORY 9: PRODUCTIVITY & SPECIALIZED AI TOOLS
  granola: {
    key: "granola",
    name: "Granola",
    type: "Meeting Notes Tool",
    category: "Meeting Productivity, AI Transcription, Note Taking",
    description:
      "An AI notepad that transforms meeting audio into polished, shareable summaries and action items without needing a bot to join the call.",
    image:
      "https://global-uploads.webflow.com/627a00f4a4ac843f8529275e/63792378686f1b3e8a713433_Granola%20Logo.png",
  },
  notebookLM: {
    key: "notebookLM",
    name: "NotebookLM",
    type: "AI Notetaking and Research Assistant",
    category: "Productivity, AI Assistant, Research Tool, Notetaking",
    description:
      "A Google AI experiment designed to help users process information, analyze notes, and generate content from their uploaded documents and sources.",
    image: "https://notebooklm.google/images/icon.png",
  },
  firefliesAI: {
    key: "firefliesAI",
    name: "Fireflies.ai",
    type: "AI Meeting Assistant",
    category:
      "Meeting Productivity, AI Transcription, Note Taking, Conversation Intelligence, AI Agent",
    description:
      "An AI meeting assistant that joins your calls to record, transcribe, summarize, and analyze voice conversations. It integrates with various calendar and video-conferencing platforms to automate note-taking and fill out CRMs, allowing teams to focus on the conversation.",
    image: "https://fireflies.ai/images/logo-dark-2.png",
  },
};

export default platformConfig;
